import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TablePagination,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  SupportAgent as HelpDeskIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const STATUS_COLORS = {
  new: 'info',
  work_in_progress: 'warning',
  waiting_on_customer: 'secondary',
  resolved: 'success',
  closed: 'default',
  deferred: 'default',
  bug: 'error'
};

const PRIORITY_COLORS = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error'
};

const emptyForm = {
  subject: '',
  description: '',
  status: 'new',
  priority: 'medium',
  target_type: 'application',
  software_application_id: '',
  service_framework_id: '',
  requester_name: '',
  requester_email: ''
};

const labelFor = (list, key) => list.find((item) => item.key === key)?.label || key;

const AllTickets = () => {
  const { can } = usePermissions();
  const canView = can('helpdesk', 'view');
  const canAdd = can('helpdesk', 'add');
  const canEdit = can('helpdesk', 'edit');
  const canDelete = can('helpdesk', 'delete');

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    statuses: [],
    priorities: [],
    target_types: [],
    applications: [],
    services: []
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create | edit | view
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadMeta = async () => {
    try {
      const res = await api.get('/helpdesk/meta');
      setMeta(res.data?.data || {
        statuses: [],
        priorities: [],
        target_types: [],
        applications: [],
        services: []
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load help desk options');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/helpdesk/tickets', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          priority: priorityFilter !== 'all' ? priorityFilter : undefined,
          target_type: targetFilter !== 'all' ? targetFilter : undefined,
          search: search.trim() || undefined
        }
      });
      setRows(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) loadMeta();
  }, [canView]);

  useEffect(() => {
    if (canView) load();
  }, [canView, page, rowsPerPage, statusFilter, priorityFilter, targetFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      subject: row.subject || '',
      description: row.description || '',
      status: row.status || 'new',
      priority: row.priority || 'medium',
      target_type: row.target_type || 'application',
      software_application_id: row.software_application_id || '',
      service_framework_id: row.service_framework_id || '',
      requester_name: row.requester_name || '',
      requester_email: row.requester_email || ''
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const openView = (row) => {
    setEditing(row);
    setForm({
      subject: row.subject || '',
      description: row.description || '',
      status: row.status || 'new',
      priority: row.priority || 'medium',
      target_type: row.target_type || 'application',
      software_application_id: row.software_application_id || '',
      service_framework_id: row.service_framework_id || '',
      requester_name: row.requester_name || '',
      requester_email: row.requester_email || ''
    });
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (form.target_type === 'application' && !form.software_application_id) {
      toast.error('Select a software application');
      return;
    }
    if (form.target_type === 'service' && !form.service_framework_id) {
      toast.error('Select a service / framework');
      return;
    }

    const payload = {
      subject: form.subject.trim(),
      description: form.description,
      status: form.status,
      priority: form.priority,
      target_type: form.target_type,
      software_application_id: form.target_type === 'application' ? form.software_application_id : null,
      service_framework_id: form.target_type === 'service' ? form.service_framework_id : null,
      requester_name: form.requester_name || null,
      requester_email: form.requester_email || null
    };

    setSaving(true);
    try {
      if (dialogMode === 'edit' && editing) {
        await api.put(`/helpdesk/tickets/${editing.id}`, payload);
        toast.success('Ticket updated');
      } else {
        await api.post('/helpdesk/tickets', payload);
        toast.success('Ticket created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ticket ${row.ticket_number}?`)) return;
    try {
      await api.delete(`/helpdesk/tickets/${row.id}`);
      toast.success('Ticket deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const targetLabel = (row) => {
    if (row.target_type === 'application') {
      return row.application?.name || 'Application';
    }
    return row.service?.name || 'Service';
  };

  const readOnly = dialogMode === 'view';

  if (!canView) {
    return (
      <Alert severity="warning">You do not have permission to view Help Desk tickets.</Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HelpDeskIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>All Tickets</Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage help desk tickets for software applications and services.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          {canAdd && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New Ticket
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(0);
              load();
            }
          }}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">All</MenuItem>
            {meta.statuses.map((s) => (
              <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            label="Priority"
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">All</MenuItem>
            {meta.priorities.map((p) => (
              <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Target</InputLabel>
          <Select
            label="Target"
            value={targetFilter}
            onChange={(e) => { setTargetFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">All</MenuItem>
            {meta.target_types.map((t) => (
              <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          onClick={() => {
            setPage(0);
            load();
          }}
        >
          Apply
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Requester</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No tickets found
                </TableCell>
              </TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography fontWeight={700} sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {row.ticket_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{row.subject}</Typography>
                  {row.description ? (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 280 }}>
                      {row.description}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={row.target_type === 'application' ? 'App' : 'Service'}
                    sx={{ mr: 0.75 }}
                  />
                  <Typography variant="body2" component="span">{targetLabel(row)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLORS[row.status] || 'default'}
                    label={labelFor(meta.statuses, row.status)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={PRIORITY_COLORS[row.priority] || 'default'}
                    label={labelFor(meta.priorities, row.priority)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.requester_name || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.requester_email || ''}</Typography>
                </TableCell>
                <TableCell>
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => openView(row)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'New Ticket'}
          {dialogMode === 'edit' && `Edit Ticket ${editing?.ticket_number || ''}`}
          {dialogMode === 'view' && `Ticket ${editing?.ticket_number || ''}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Subject"
              required
              fullWidth
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              disabled={readOnly}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={readOnly}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {meta.statuses.map((s) => (
                    <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Priority</InputLabel>
                <Select
                  label="Priority"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                >
                  {meta.priorities.map((p) => (
                    <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel>Related to</InputLabel>
              <Select
                label="Related to"
                value={form.target_type}
                onChange={(e) => setForm({
                  ...form,
                  target_type: e.target.value,
                  software_application_id: '',
                  service_framework_id: ''
                })}
              >
                {meta.target_types.map((t) => (
                  <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.target_type === 'application' ? (
              <FormControl fullWidth required disabled={readOnly}>
                <InputLabel>Software Application</InputLabel>
                <Select
                  label="Software Application"
                  value={form.software_application_id}
                  onChange={(e) => setForm({ ...form, software_application_id: e.target.value })}
                >
                  {meta.applications.map((app) => (
                    <MenuItem key={app.id} value={app.id}>{app.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth required disabled={readOnly}>
                <InputLabel>Service / Framework</InputLabel>
                <Select
                  label="Service / Framework"
                  value={form.service_framework_id}
                  onChange={(e) => setForm({ ...form, service_framework_id: e.target.value })}
                >
                  {meta.services.map((svc) => (
                    <MenuItem key={svc.id} value={svc.id}>
                      {svc.name}{svc.type ? ` (${svc.type})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Requester name"
                fullWidth
                value={form.requester_name}
                onChange={(e) => setForm({ ...form, requester_name: e.target.value })}
                disabled={readOnly}
              />
              <TextField
                label="Requester email"
                fullWidth
                type="email"
                value={form.requester_email}
                onChange={(e) => setForm({ ...form, requester_email: e.target.value })}
                disabled={readOnly}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly && (
            <Button variant="contained" onClick={handleSave} disabled={saving || !form.subject.trim()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
          {readOnly && canEdit && (
            <Button
              variant="contained"
              onClick={() => setDialogMode('edit')}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllTickets;
